import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, MapPinIcon } from '@heroicons/react/24/outline';

export default function CheckInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gymIdParam = searchParams.get('gym_id');
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Requesting your location to verify check-in...');
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);

  // Manual Check-In State
  const [isManual, setIsManual] = useState(false);
  const [members, setMembers] = useState([]);
  const [manualMemberId, setManualMemberId] = useState('');
  const [manualReason, setManualReason] = useState('');

  useEffect(() => {
    fetchMembers();
    if (user?.role === 'member' || gymIdParam) {
      performGeoCheckIn();
    }
  }, [user?.role, gymIdParam]);

  const fetchMembers = async () => {
    try {
      const response = await api.get('/api/attendance/members');
      setMembers(response.data.members || []);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  const performGeoCheckIn = () => {
    setLoading(true);
    setError('');
    setSuccessData(null);
    setStatusMessage('Requesting your location to verify check-in...');

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          submitCheckIn(lat, lng);
        },
        (geoErr) => {
          console.warn('Geolocation denied or unavailable:', geoErr);
          setStatusMessage('Location permission unavailable. Submitting check-in...');
          submitCheckIn(null, null);
        },
        { timeout: 8000 }
      );
    } else {
      submitCheckIn(null, null);
    }
  };

  const submitCheckIn = async (lat, lng) => {
    try {
      const targetGymId = gymIdParam ? parseInt(gymIdParam, 10) : (user?.gym_id || null);
      const res = await api.post('/api/attendance/checkin', {
        gym_id: targetGymId,
        latitude: lat,
        longitude: lng
      });

      setSuccessData(res.data.attendance);
      setStatusMessage(res.data.message || 'Check-in successful!');
    } catch (err) {
      console.error('Check-in failed:', err);
      const errRes = err.response?.data;
      if (errRes?.attendance) {
        setSuccessData(errRes.attendance);
      }
      setError(errRes?.error || 'Check-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualMemberId) {
      setError('Please select a member.');
      return;
    }
    if (!manualReason.trim()) {
      setError('Please provide a reason for manual check-in.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/api/attendance/manual-checkin', {
        member_id: manualMemberId,
        reason: manualReason.trim()
      });
      navigate('/attendance');
    } catch (err) {
      setError(err.response?.data?.error || 'Manual check-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gym Attendance Check-In</h1>
          <p className="text-xs text-gray-600">Scan QR Code check-in verification</p>
        </div>
        <button
          onClick={() => navigate('/attendance')}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-xs font-medium transition-colors flex items-center gap-2"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to Dashboard
        </button>
      </div>

      {/* Main Status & Auto Check-In Card */}
      <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-sm text-center space-y-6">
        {loading && (
          <div className="space-y-4">
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm font-medium text-gray-700">{statusMessage}</p>
          </div>
        )}

        {!loading && successData && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
              <CheckCircleIcon className="w-10 h-10" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                ✅ Checked in at {successData.gym_name || 'Gym'}
              </h2>
              <p className="text-xs text-gray-600 mt-1">
                Time: {new Date(successData.check_in_time).toLocaleTimeString()}
              </p>
            </div>

            {successData.location_flagged && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded-xl flex items-center gap-2 justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span>Notice: Location flagged (scanned outside standard geofence range). Recorded for review.</span>
              </div>
            )}

            {successData.location_unavailable && (
              <div className="bg-gray-50 border border-gray-200 text-gray-700 text-xs p-3 rounded-xl flex items-center gap-2 justify-center">
                <MapPinIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                <span>Location permission not provided. Check-in recorded without GPS verification.</span>
              </div>
            )}
          </div>
        )}

        {!loading && error && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
              <ExclamationTriangleIcon className="w-10 h-10" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-red-800">Check-in Error</h2>
              <p className="text-xs text-red-600 mt-1">{error}</p>
            </div>
            <button
              onClick={performGeoCheckIn}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 px-5 rounded-xl text-xs transition-colors"
            >
              Retry Check-in
            </button>
          </div>
        )}
      </div>

      {/* Manual Check-in Form Toggle for Front Desk */}
      <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900">Manual Front-Desk Fallback</h2>
          <button
            type="button"
            onClick={() => setIsManual(!isManual)}
            className="text-xs font-semibold text-orange-600 hover:text-orange-700 underline"
          >
            {isManual ? 'Hide Manual Form' : 'Show Manual Form'}
          </button>
        </div>

        {isManual && (
          <form onSubmit={handleManualSubmit} className="space-y-4 pt-3 border-t border-gray-100">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">Select Member *</label>
              <select
                required
                value={manualMemberId}
                onChange={(e) => setManualMemberId(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none"
              >
                <option value="">Select a member</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} - {member.phone}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">Manual Reason (Logged for Audit) *</label>
              <input
                type="text"
                required
                placeholder="e.g. Member phone battery dead"
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 px-5 rounded-xl text-xs transition-colors"
            >
              {loading ? 'Processing...' : 'Submit Manual Check-In'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}